// Package imports:
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/pro-solid-svg-icons';
// Component imports:
import DisplayBox from '../DisplayBox/DisplayBox';
import Link from '../../ui-elements/Link/Link';
import VerticalTable from '../../ui-elements/Table/VerticalTable';
import Table from '../../ui-elements/Table/Table';
import Alert from '../../ui-elements/Alert/Alert';
// Service imports:
// Type imports:
import { IEventReport } from '../../types/KeldanTypes';
import { VehicleSearchResult } from '../../types/VehicleTypes';

interface IProps {
    eventId?: string,
    data: IEventReport<VehicleSearchResult>,
    isExample?: boolean
}

const VehicleReportBody: React.FC<IProps> = ({
    eventId,
    data,
    isExample = false
}) => {
    const padDateString = (date?: string) => {
		if (!date) return '-';
        date = date.replaceAll('/', '.')
		if (date[2] !== '.') date = date.padStart(date.length + 1, '0')
		if (date[5] !== '.') date = date.substring(0,3) + '0' + date.substring(3)
		return date;
	}

    return (
        <>
            <DisplayBox
                title="Grunnupplýsingar"
                asideComponent={
                    <Link
                        targetBlank
                        url={`/Pdf/Okutaekjaskra/${eventId}`}
                        disabled={isExample}
                    >
                        <FontAwesomeIcon icon={faFilePdf} /> Sækja skýrslu
                    </Link>
                }
                className="vehicle-report-box"
                grayBackground={isExample}
            >
                {/* Grunnupplýsingar */}
                <div className='table-section'>
                    <div className='italic-text'>
                        Skýrsla keypt: {data.eventDate}
                    </div>
                    <div className='vertical-table-wrapper'>
                        <VerticalTable
                            fallbackSymbol
                            data={[data.overview?.car]}
                            columns={[{
                                title: 'Eigandi',
                                dataAccessor: ({owners}) => (owners ? owners[0]?.fullname : null),
                            }, {
                                title: 'Kennitala',
                                dataAccessor: ({owners}) => (owners ? owners[0]?.persidno : null),
                            }, {
                                title: 'Lögheimili',
                                dataAccessor: ({owners}) => (owners ? owners[0]?.address : null),
                            }, {
                                title: 'Tegund',
                                dataAccessor: ({make, vehcom}) => (make + ' ' + vehcom),
                            }, {
                                title: 'Árgerð',
                                dataAccessor: ({modelyear}) => (modelyear),
                            }, {
                                title: 'Skráningarnúmer',
                                dataAccessor: ({regno}) => (regno),
                            }, {
                                title: 'Fastanúmer',
                                dataAccessor: ({permno}) => (permno),
                            }, {
                                title: 'Framleiðandi',
                                dataAccessor: ({manufacturer}) => (manufacturer),
                            }, {
                                title: 'Framleiðsluland',
                                dataAccessor: ({country}) => (country),
                            }
                            ]}
                            tableSize="lg"
                        />
                        <VerticalTable
                            fallbackSymbol
                            data={[data.overview?.car]}
                            columns={[{
                                title: 'Framleiðsluár',
                                dataAccessor: ({productyear}) => (productyear),
                            }, {
                                title: 'Nýskráningardagur',
                                dataAccessor: ({firstregdate}) => (firstregdate),
                            }, {
                                title: 'Næsta aðalskoðun',
                                dataAccessor: ({nextinspectiondate}) => (nextinspectiondate),
                            }, {
                                title: 'Litur',
                                dataAccessor: ({color}) => (color),
                            }, {
                                title: 'Staða',
                                dataAccessor: ({vehiclestatus}) => (vehiclestatus),
                            }, {
                                title: 'Veðbönd',
                                dataAccessor: ({hasEncumbrances}) => ((hasEncumbrances === '1') ? 'Já' : 'Nei'),
                            }, {
                                title: 'Númerastaða	',
                                dataAccessor: ({platestatus}) => (platestatus),
                            }, {
                                title: 'Tryggingar',
                                dataAccessor: ({insurancestatus}) => ((insurancestatus === '1') ? 'Já' : 'Nei'),
                            }, {
                                title: 'Tryggingafélag',
                                dataAccessor: ({insurancecompany}) => (insurancecompany),
                            }
                            ]}
                            tableSize="lg"
                        />
                    </div>
                </div>

                {/* Nánari upplýsingar */}
                <div className='table-section'>
                    <h4>Nánari upplýsingar</h4>
                    <div className='vertical-table-wrapper'>
                        <VerticalTable
                            fallbackSymbol
                            data={[data.overview?.car]}
                            columns={[{
                                title: 'Verksmiðjunúmer',
                                dataAccessor: ({vin}) => (vin),
                            }, {
                                title: 'Gerðarnúmer',
                                dataAccessor: ({typeno}) => (typeno),
                            }, {
                                title: 'Gerðarviðurkenningarnr.',
                                dataAccessor: ({typeapproval}) => (typeapproval),
                            }, {
                                title: 'Gerðarviðurkenningarviðb.',
                                dataAccessor: ({typeapprovalextension}) => (typeapprovalextension),
                            }, {
                                title: 'ESB Gerðarviðurkenningarnr.',
                                dataAccessor: ({eutypeapproval}) => (eutypeapproval),
                            }, {
                                title: 'Skattflokkur',
                                dataAccessor: ({taxgroup}) => (taxgroup),
                            }, {
                                title: 'Forskráningardagur',
                                dataAccessor: ({preregdate}) => (preregdate),
                            }, {
                                title: 'Afskráningardagur',
                                dataAccessor: ({deregdate}) => (deregdate),
                            }, {
                                title: 'Endurskráningardagur',
                                dataAccessor: ({reregdate}) => (reregdate),
                            }
                            ]}
                            tableSize="lg"
                        />
                        <VerticalTable
                            fallbackSymbol
                            data={[data.overview?.car]}
                            columns={[{
                                title: 'Kaupdagsetning',
                                dataAccessor: ({owners}) => {
                                    if(owners){
                                        const item = owners.find(x => x.current === '1');
                                        return item?.purchasedate;
                                    }
                                    else return null;
                                },
                            }, {
                                title: 'Skráning í umferð',
                                dataAccessor: ({firstregdate}) => (firstregdate),
                            }, {
                                title: 'Ökutækisflokkur',
                                dataAccessor: ({technical}) => (technical.vehgroup),
                            }, {
                                title: 'Breytt bifreið',
                                dataAccessor: ({rebuilt}) => (rebuilt === "1" ? 'Já' : 'Nei'),
                            }, {
                                title: 'Torfærubifreið',
                                dataAccessor: ({offroad}) => (offroad === "1" ? 'Já' : 'Nei'),
                            }, {
                                title: 'Skráningarflokkur',
                                dataAccessor: ({regtype}) => (regtype),
                            }, {
                                title: 'Tjónabifreið',
                                dataAccessor: ({hasdisasters}) => (hasdisasters === "1" ? 'Já' : 'Nei'),
                            }, {
                                title: 'Viðgerð tjónabifreið',
                                dataAccessor: ({fixed}) => (fixed === "1" ? 'Já' : 'Nei'),
                            }, {
                                title: 'Tegund tjónabifreiðar',
                                dataAccessor: ({disastertype}) => (disastertype),
                            }
                            ]}
                            tableSize="lg"
                        />
                    </div>
                </div>

                {/* Tæknilegar upplýsingar */}
                <div className='table-section'>
                    <h4>Tæknilegar upplýsingar</h4>
                    <div className='vertical-table-wrapper'>
                        <VerticalTable
                            fallbackSymbol
                            data={[data.overview?.car?.technical]}
                            columns={[{
                                title: 'Eiginþyngd',
                                dataAccessor: ({mass}) => (mass.massinro + ' kg'),
                            }, {
                                title: 'Heildarþyngd',
                                dataAccessor: ({mass}) => (mass.massladen + ' kg'),
                            }, {
                                title: 'Heildarþyngd ása',
                                dataAccessor: ({mass}) => {
                                    let sum = 0;
                                    if(mass.massdaxle1) sum += parseInt(mass.massdaxle1);
                                    if(mass.massdaxle2) sum += parseInt(mass.massdaxle2);
                                    if(mass.massdaxle3) sum += parseInt(mass.massdaxle3);
                                    if(mass.massdaxle4) sum += parseInt(mass.massdaxle4);
                                if(mass.massdaxle5) sum += parseInt(mass.massdaxle5);
                                    return sum.toString() + ' kg';
                                },
                            }, {
                                title: 'Burðargeta ökutækis',
                                dataAccessor: ({mass}) => (mass.masscapacity + ' kg'),
                            }, {
                                title: 'Þyngd óhemlaðs eftirvagns',
                                dataAccessor: ({mass}) => (mass.massoftrunbr + ' kg'),
                            }, {
                                title: 'Þyngd hemlaðs eftirvagns',
                                dataAccessor: ({mass}) => (mass.massoftrbr + ' kg'),
                            }, {
                                title: 'Lengd',
                                dataAccessor: ({size}) => (size.length + ' mm'),
                            }, {
                                title: 'Breidd',
                                dataAccessor: ({size}) => (size.width + ' mm'),
                            }, {
                                title: 'Hæð',
                                dataAccessor: ({size}) => (size.height + ' mm'),
                            }, {
                                title: 'Dyrafjöldi',
                                dataAccessor: ({doorsno}) => (doorsno),
                            } , {
                                title: 'Farþegar hjá ökumanni',
                                dataAccessor: ({passbydr}) => (passbydr),
                            }, {
                                title: 'Farþegafjöldi',
                                dataAccessor: ({pass}) => (pass),
                            }
                            ]}
                            tableSize="lg"
                        />
                        <VerticalTable
                            fallbackSymbol
                            data={[data.overview?.car?.technical]}
                            columns={[{
                                title: 'Sætafjöldi',
                                dataAccessor: ({seatno}) => (seatno),
                            }, {
                                title: 'Gírafjöldi',
                                dataAccessor: ({noofgears}) => (noofgears),
                            }, {
                                title: 'Orkugjafi',
                                dataAccessor: ({engine}) => (engine),
                            }, {
                                title: 'Hámarks afköst',
                                dataAccessor: ({maxnetpow}) => (maxnetpow + ' kW'),
                            }, {
                                title: 'Vélargerð',
                                dataAccessor: ({workingpr}) => (workingpr),
                            }, {
                                title: 'Vélarnúmer',
                                dataAccessor: ({enginecode}) => (enginecode),
                            }, {
                                title: 'Slagrými',
                                dataAccessor: ({capacity}) => (capacity + ' cm3'),
                            }, {
                                title: 'CO2 Útblástur',
                                dataAccessor: ({co2}) => (co2 + ' g/km'),
                            }, {
                                title: 'Hjólbarðastærð',
                                dataAccessor: ({tyre}) => (tyre.tyreaxle1),
                            }, {
                                title: 'Felgustærð',
                                dataAccessor: ({axle}) => (axle.wheelaxle1),
                            }, {
                                title: 'Gerð yfirbyggingar',
                                dataAccessor: ({typeofbody}) => (typeofbody),
                            }
                            ]}
                            tableSize="lg"
                        />
                    </div>
                </div>

                {/* Skoðanir */}
                <div className="table-section">
                    <h4>Skoðanir</h4>
                    <Table
                        data={data.overview?.car?.inspections}
                        columns={[{
                            title: 'Skoðunarstaður',
                            dataAccessor: ({station}) => (station),
                            textAlign: 'left'
                        }, {
                            title: 'Tegund',
                            dataAccessor: ({type}) => (type),
                            textAlign: 'left'
                        }, {
                            title: 'skoðunardagur',
                            dataAccessor: ({date}) => (date),
                        }, {
                            title: 'staða mælis',
                            dataAccessor: ({odometer}) => (odometer),
                        }, {
                            title: 'Niðurstaða',
                            dataAccessor: ({result}) => (result),
                        }]}
                        tableSize="lg"
                    />
                </div>

                {/* Eigendur */}
                <div className="table-section">
                    <h4>Eigendasaga</h4>
                    <Table
                        data={data.overview?.car?.owners}
                        columns={[{
                            title: 'Eigandi',
                            dataAccessor: ({fullname}) => (fullname),
                            textAlign: 'left'
                        }, {
                            title: 'Kennitala',
                            dataAccessor: ({persidno}) => (persidno),
                            textAlign: 'left'
                        }, {
                            title: 'Póstnúmer',
                            dataAccessor: ({postalcode}) => (postalcode),
                        }, {
                            title: 'Kaupdagur',
                            dataAccessor: ({purchasedate}) => (purchasedate),
                        }, {
                            title: 'Móttökudagur',
                            dataAccessor: ({receptiondate}) => (receptiondate),
                        }]}
                        tableSize="lg"
                    />
                </div>

                {/* Umráðasaga */}
                <div className="table-section">
                    <h4>Umráðasaga</h4>
                    <Table
                        data={data.overview?.car?.operators}
                        columns={[{
                            title: 'Umráðamaður',
                            dataAccessor: ({fullname}) => (fullname),
                            textAlign: 'left'
                        }, {
                            title: 'Kennitala',
                            dataAccessor: ({persidno}) => (persidno),
                            textAlign: 'left'
                        }, {
                            title: 'byrjar',
                            dataAccessor: ({startdate}) => (startdate),
                        }, {
                            title: 'Lýkur',
                            dataAccessor: ({enddate}) => (enddate),
                        }]}
                        tableSize="lg"
                    />
                </div>
            </DisplayBox>
            {data.overview?.bondDetails?.bondDetails?.bondDetails?.bonds &&
                <>
                    <h2>Veðbandayfirlit</h2>
                    <DisplayBox className='vehicle-report-box'>
                        {data.overview.bondDetails.bondDetails.bondDetails.bonds.map((bond, index) => (
                            <div className='report-section' key={index}>
                                <h4>{bond.number}. veðréttur</h4>
                                <div className='vertical-table-wrapper'>
                                    <VerticalTable
                                        data={[bond]}
                                        columns={[{
                                            title: 'Upphæð',
                                            dataAccessor: ({amount, currency}) => `${amount} ${currency}`
                                        }, {
                                            title: 'Myntgengi',
                                            dataAccessor: ({currency_rate}) => currency_rate
                                        }, {
                                            title: 'Númer',
                                            dataAccessor: ({id}) => id
                                        }, {
                                            title: 'Tegund',
                                            dataAccessor: ({type}) => type
                                        }, {
                                            title: 'Verðbinding',
                                            dataAccessor: ({index_status}) => index_status
                                        }]}
                                        tableSize='lg'
                                    />
                                    <VerticalTable
                                        data={[bond]}
                                        columns={[{
                                            title: 'Vísitölustig',
                                            dataAccessor: ({index_state}) => index_state
                                        }, {
                                            title: 'Veðhafi',
                                            dataAccessor: ({loaner}) => loaner
                                        }, {
                                            title: 'Útgáfudagur',
                                            dataAccessor: ({published}) => padDateString(published)
                                        }, {
                                            title: 'Móttökudagur',
                                            dataAccessor: ({received}) => padDateString(received)
                                        }]}
                                        tableSize='lg'
                                    />
                                </div>
                            </div>  
                        ))}
                    </DisplayBox>
                </>
            }
            <Alert
                type='info'
                headText='Athugið'
                text={
                    <>
                        <p>Birtar upplýsingar eru sóttar með tengingu við Ökutækjaskrá. 
                        Allar upplýsingar eru veittar samkvæmt starfsreglum Umferðarstofu og í samræmi við lög og reglur um persónuvernd og meðferð persónuupplýsinga.
                        </p>
                        <p>
                            Reynt er að tryggja að upplýsingar séu réttar en Keldan getur ekki ábyrgst áreiðanleika þeirra.
                        </p>
                    </>
                }
            />
        </>
    );
}

export default VehicleReportBody;